"use client";
import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { Menu, X, Phone, Mail } from "lucide-react";
import { motion } from "framer-motion";
import siteConfig from "@/lib/siteConfig";

export default function MenuNav() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [stickyBarHeight, setStickyBarHeight] = useState(0);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  useEffect(() => {
    const updateStickyBarHeight = () => {
      const stickyBar = document.getElementById("sticky-top-bar");
      if (stickyBar) {
        setStickyBarHeight(stickyBar.offsetHeight);
      }
    };

    updateStickyBarHeight();
    window.addEventListener("resize", updateStickyBarHeight);

    return () => window.removeEventListener("resize", updateStickyBarHeight);
  }, []);

  return (
    <div className="relative">
      <div
        id="sticky-top-bar"
        className="bg-[#4d9c2f] text-white fixed top-0 left-0 right-0 z-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-center sm:justify-end items-center h-auto sm:h-10 space-y-2 sm:space-y-0 sm:space-x-6 py-2">
          <div className="flex items-center">
            <Phone className="w-4 h-4 mr-2" />
            <span>{siteConfig.contact.phone}</span>
          </div>
          <div className="flex items-center">
            <Mail className="w-4 h-4 mr-2" />
            <span>{siteConfig.contact.email}</span>
          </div>
        </div>
      </div>

      <nav
        className="bg-white shadow-md fixed left-0 right-0 z-40"
        style={{ top: `${stickyBarHeight}px` }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex-shrink-0 flex items-center">
              <Link href="/" className="text-2xl font-bold text-primary">
                <img src="/logo.png" alt="logo" className="w-36 h-10" />
              </Link>
            </div>

            <div className="hidden sm:ml-6 sm:flex sm:space-x-8 items-center">
              <NavLink href="/#products">Products</NavLink>
              <NavLink href="/about-us">About Us</NavLink>
              <NavLink href="/contact">Contact</NavLink>
              <NavLink href="/blog">Blog</NavLink>
              <Button
                asChild
                size="sm"
                className="bg-primary hover:bg-primary/90 text-primary-foreground group w-42"
              >
                <Link href="/contact">
                  Get Your Free Quote
                  <motion.span
                    className="ml-2"
                    initial={{ x: 0 }}
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    →
                  </motion.span>
                </Link>
              </Button>

              {/* <Link
                href="/contact"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#4d9c2f] hover:bg-[#3e8127]"
              >
                10% Price Beat Guarantee!
              </Link> */}
            </div>

            {/* Mobile Menu Button */}
            <div className="sm:hidden flex items-center">
              <button
                onClick={toggleMenu}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
                aria-expanded={isMenuOpen}
              >
                <span className="sr-only">Open main menu</span>
                {isMenuOpen ? (
                  <X className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <Menu className="block h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>
        {/* Mobile menu */}
        <div className={`sm:hidden ${isMenuOpen ? "block" : "hidden"}`}>
          <div className="pt-2 pb-3 space-y-1">
            <MobileNavLink href="/#products" onClick={toggleMenu}>
              Products
            </MobileNavLink>
            <MobileNavLink href="/about-us" onClick={toggleMenu}>
              About Us
            </MobileNavLink>
            <MobileNavLink href="/blog" onClick={toggleMenu}>
              Blog
            </MobileNavLink>
            <MobileNavLink href="/contact" onClick={toggleMenu}>
              Contact
            </MobileNavLink>
          </div>
        </div>
      </nav>
      <div style={{ height: `${stickyBarHeight + 64}px` }}></div>
    </div>
  );
}

function NavLink({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <Link
      href={href}
      className="inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700"
    >
      {children}
    </Link>
  );
}

function MobileNavLink({
  href,
  children,
  onClick,
}: {
  href: string;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <Link
      href={href}
      className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-50 hover:border-gray-300"
      onClick={onClick}
    >
      {children}
    </Link>
  );
}
